import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import { removeWeaponDocuments } from './weaponDocumentService';

export interface WeaponData {
  id: string;
  weaponId: string;
  weaponType: string;
  serialNumber: string;
  manufacturer?: string;
  model?: string;
  caliber?: string;
  purchaseDate?: Date | any;
  purchasePrice?: number;
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  status: 'available' | 'deployed' | 'maintenance' | 'retired';
  notes?: string;
  image?: string;
  createdAt: Date | any;
  updatedAt?: Date | any;
  currentDeployment?: {
    clientId: string;
    clientName: string;
    clientType: 'Individual' | 'Factory' | 'Bank';
    clientPath: string;
    deployedDate: Date | any;
    deployedBy: string;
  };
  history?: Array<{
    clientId: string;
    clientName: string;
    clientType: 'Individual' | 'Factory' | 'Bank';
    clientPath: string;
    deployedDate: Date | any;
    returnedDate: Date | any;
    deployedBy: string;
    returnedBy?: string;
    status: 'deployed' | 'returned';
  }>;
}

export interface WeaponDeploymentData {
  weaponId: string;
  clientId: string;
  clientName: string;
  clientType: 'Individual' | 'Factory' | 'Bank';
  clientPath: string;
  deploymentDate: Date;
}

/**
 * Get all weapons for a company
 */
export const fetchWeapons = async (companyId: string): Promise<WeaponData[]> => {
  const weaponsRef = collection(db, 'weapons', companyId, 'companyWeapons');
  const q = query(weaponsRef, orderBy('weaponId'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WeaponData[];
};

/**
 * Get a single weapon by ID
 */
export const getWeapon = async (weaponId: string, companyId: string): Promise<WeaponData | null> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  const weaponSnap = await getDoc(weaponRef);
  
  if (!weaponSnap.exists()) {
    return null;
  }
  
  return {
    id: weaponSnap.id,
    ...weaponSnap.data(),
  } as WeaponData;
};

/**
 * Create a new weapon
 */
export const createWeapon = async (
  weaponData: Omit<WeaponData, 'id' | 'createdAt' | 'updatedAt'>,
  companyId: string
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const weaponsRef = collection(db, 'weapons', companyId, 'companyWeapons');
  const weaponRef = doc(weaponsRef);
  
  const newWeapon: Omit<WeaponData, 'id'> = {
    ...weaponData,
    status: weaponData.status || 'available',
    condition: weaponData.condition || 'good',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(weaponRef, newWeapon);
  return weaponRef.id;
};

/**
 * Update a weapon
 */
export const updateWeapon = async (
  weaponId: string,
  weaponData: Partial<WeaponData>,
  companyId: string
): Promise<void> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  
  await updateDoc(weaponRef, {
    ...weaponData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a weapon
 */
export const deleteWeapon = async (weaponId: string, companyId: string): Promise<void> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  await deleteDoc(weaponRef);
};

/**
 * Deploy weapon to client
 */
export const deployWeaponToClient = async (
  deploymentData: WeaponDeploymentData,
  companyId: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', deploymentData.weaponId);
  const weaponSnap = await getDoc(weaponRef);

  if (!weaponSnap.exists()) {
    throw new Error('Weapon not found');
  }

  const weaponData = weaponSnap.data() as WeaponData;
  const history = weaponData.history || [];

  // If weapon is currently deployed, move to history
  if (weaponData.currentDeployment) {
    // Remove documents when weapon is redeployed to another client
    try {
      await removeWeaponDocuments(deploymentData.weaponId, companyId);
    } catch (error) {
      console.error(`Error removing documents for weapon ${deploymentData.weaponId}:`, error);
    }

    history.push({
      clientId: weaponData.currentDeployment.clientId,
      clientName: weaponData.currentDeployment.clientName,
      clientType: weaponData.currentDeployment.clientType,
      clientPath: weaponData.currentDeployment.clientPath,
      deployedDate: weaponData.currentDeployment.deployedDate,
      returnedDate: new Date(),
      deployedBy: weaponData.currentDeployment.deployedBy,
      returnedBy: user.uid,
      status: 'returned',
    });
  }

  // Update weapon with new deployment
  await updateDoc(weaponRef, {
    status: 'deployed',
    currentDeployment: {
      clientId: deploymentData.clientId,
      clientName: deploymentData.clientName,
      clientType: deploymentData.clientType,
      clientPath: deploymentData.clientPath,
      deployedDate: deploymentData.deploymentDate,
      deployedBy: user.uid,
    },
    history: history,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Return weapon from client
 */
export const returnWeaponFromClient = async (
  weaponId: string,
  companyId: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  const weaponSnap = await getDoc(weaponRef);

  if (!weaponSnap.exists()) {
    throw new Error('Weapon not found');
  }

  const weaponData = weaponSnap.data() as WeaponData;
  const history = weaponData.history || [];

  // Move current deployment to history
  if (weaponData.currentDeployment) {
    // Remove documents when weapon is returned
    try {
      await removeWeaponDocuments(weaponId, companyId);
    } catch (error) {
      console.error(`Error removing documents for weapon ${weaponId}:`, error);
    }

    history.push({
      clientId: weaponData.currentDeployment.clientId,
      clientName: weaponData.currentDeployment.clientName,
      clientType: weaponData.currentDeployment.clientType,
      clientPath: weaponData.currentDeployment.clientPath,
      deployedDate: weaponData.currentDeployment.deployedDate,
      returnedDate: new Date(),
      deployedBy: weaponData.currentDeployment.deployedBy,
      returnedBy: user.uid,
      status: 'returned',
    });
  }

  // Update weapon status
  await updateDoc(weaponRef, {
    status: 'available',
    currentDeployment: null,
    history: history,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Get weapon count by status
 */
export const getWeaponCount = async (companyId: string): Promise<{
  total: number;
  available: number;
  deployed: number;
  maintenance: number;
  retired: number;
}> => {
  const weapons = await fetchWeapons(companyId);
  
  return {
    total: weapons.length,
    available: weapons.filter((w) => w.status === 'available').length,
    deployed: weapons.filter((w) => w.status === 'deployed').length,
    maintenance: weapons.filter((w) => w.status === 'maintenance').length,
    retired: weapons.filter((w) => w.status === 'retired').length,
  };
};

